import type { ApplicationDto, IncomeType, UtmMetricDto } from '@/api/backend/users/applications';
import type { ApiSchema } from '@/types/api';
import type { ExpenseTypes } from '@/api/backend/public';
import type {
  AdditionalFundsPurposeEnum,
  ApplicantAsset,
  ApplicantExpense,
  ApplicantIncome,
  ApplicantLiability,
  AppUser,
  AssetTypeEnum,
  Employment,
  Financials,
  LoanApplication,
  PropertyOwnershipType,
} from '@/types';
import { frequency } from '../helpers';
import { AdditionalFundsPurpose, LiabilityType, LoanType, PropertyType, SaleStatus } from '@/helpers/const';
import { formatDateTz, stripHtml } from '@/helpers/format';
import type { AdditionalFundsRequiredProperty } from '@/types/AdditionalFundsRequiredProperty';
import type { IncomingProperty, PropertyTypeEnum } from '@/types/Property';
import { defaultAddress } from '@/helpers/builder';
import type { Applicant } from '@/types/Applicant';
import { mapIdsTypeFromApi } from '../helpers';

export type AdditionalFundsRequiredPropertyDto = ApiSchema['AdditionalFundsRequiredProperty'];
type FundsAssetFields = Pick<
  ApiSchema['FundsAssetCurrentVersion'],
  'assetType' | 'assetAmount' | 'assetDescription' | 'vehicleMake' | 'vehicleModel' | 'yearBought' | 'institution'
>;

const mapApplicantFinancials = (application: ApplicationDto, expenseTypes: ExpenseTypes) => {
  let lastAddressIndex = 0;
  const getNextAddressIndex = () => {
    lastAddressIndex++;
    return lastAddressIndex;
  };
  let lastLiabilityIndex = 0;
  const getNextLiabilityIndex = () => {
    lastLiabilityIndex++;
    return lastLiabilityIndex;
  };
  const financials: Record<string, Financials> = {};
  const fundsAdditionalIncome = (incomeType: IncomeType, applicantId: string | undefined) => {
    return (application.fundsAdditionalIncome ?? [])
      .filter((item) => item.applicantId === applicantId)
      .find((item) => item.incomeType === incomeType);
  };
  if (expenseTypes?.length) {
    expenseTypes.sort((a, b) => {
      const sortOrderA = a.sortOrder || 0;
      const sortOrderB = b.sortOrder || 0;
      return sortOrderA - sortOrderB;
    });
  }

  const getApplicantExpense = (applicantId: string | undefined) => {
    const expenseTypesMap: Record<
      string,
      { amount: number | null; frequency: string; expenseTypeId: string | undefined }
    > = {};
    for (const expenseType of expenseTypes) {
      if (!expenseType.type) {
        continue;
      }
      if (expenseTypesMap[expenseType.type]) {
        continue;
      }
      const fundExpense = (application.fundsExpenses ?? []).find(
        (item) => item.applicantId === applicantId && item.expenseTypeId === expenseType.id,
      );
      if (fundExpense?.expenseAmount === undefined || fundExpense?.expenseAmount === null) {
        continue;
      }
      expenseTypesMap[expenseType.type] = {
        amount: fundExpense?.expenseAmount ?? null,
        frequency: 'month',
        expenseTypeId: expenseType.id,
      };
    }
    return expenseTypesMap;
  };

  const getAssetPropertyShare = (propertyId: string | undefined) => {
    const share: Record<string, number> = {};
    for (const [applicantIndex, applicant] of application.applicants.entries()) {
      const propertiesOwnership = application.propertiesOwnership.find(
        (item) => item.applicantId === applicant.id && item.assetPropertyId === propertyId,
      );
      share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
    }
    return share;
  };

  const getExpenseExpectation = (applicantId: string | undefined) => {
    return application.expenseExpectations.find((item) => item.applicantId === applicantId);
  };

  for (const [applicantIndex, applicant] of application.applicants.entries()) {
    const employmentDetail = (application.employmentDetails ?? []).find((item) => item.applicantId === applicant.id);
    const incomeExpectation = application.incomeExpectations.find((item) => item.applicantId === applicant.id);
    const applicantExpense = getApplicantExpense(applicant.id);
    const expenseExpectation = getExpenseExpectation(applicant.id);
    let expenses: ApplicantExpense | null = null;
    if (
      Object.keys(applicantExpense).length ||
      expenseExpectation?.expenseAmount ||
      expenseExpectation?.changesInLivingExpenses
    ) {
      expenses = {
        ...applicantExpense,
        numberOfDependants: applicant.numberOfDependants ?? 0,
        predictedExpenseChange: {
          amount: expenseExpectation?.expenseAmount ?? 0,
          frequency: frequency[expenseExpectation?.frequency ?? 'monthly'],
        },
        expenseChangeReason: expenseExpectation?.expenseChangeReason ?? '',
        changesInLivingExpense: expenseExpectation?.changesInLivingExpenses ?? 'no_change',
      };
    }

    let income: ApplicantIncome | null = null;
    if (employmentDetail?.id && incomeExpectation?.id) {
      income = {
        applicantId: '',
        employment: [
          ...(application.fundsSalaried?.filter((fund) => fund.applicantId === applicant.id) ?? []).map(
            (employment, employmentIndex) => {
              const addressIndex = getNextAddressIndex();
              return {
                type: employment.jobType ?? 'unemployed',
                salary: {
                  amount: employment.salaryAmount ?? 0,
                  frequency: frequency[employment.salaryFrequency ?? 'monthly'],
                },
                commission: {
                  amount: employment.commissionsAmount ?? 0,
                  frequency: frequency[employment.commissionsFrequency ?? 'monthly'],
                },
                bonuses: {
                  amount: employment.bonusesAmount ?? 0,
                  frequency: frequency[employment.bonusesFrequency ?? 'monthly'],
                },
                overtime: {
                  amount: employment.overtimeAmount ?? 0,
                  frequency: frequency[employment.overtimeFrequency ?? 'monthly'],
                },
                salarySacrifice: employment.isSalarySacrifice,
                employerName: employment.nameOfEmployer,
                employerAddress: {
                  id: `applicant-${applicantIndex}-employment-${employmentIndex}-employer-address-option-${addressIndex}`,
                  index: addressIndex,
                  addressLine: employment.employerLine1 ?? '',
                  locality: employment.employerCity ?? '',
                  state: employment.employerState?.toLocaleUpperCase() ?? '',
                  postcode: employment.employerPostCode ?? '',
                  countryCode: 'AU',
                  addressManuallyCreated: true,
                },
                employedSince: employment.employedSince ?? undefined,
              };
            },
          ),
          ...(application.fundsSelfEmployed?.filter((fund) => fund.applicantId === applicant.id) ?? []).map(
            (employment, employmentIndex) => {
              const addressIndex = getNextAddressIndex();
              return {
                type: 'self_employed',
                salary: {
                  amount: employment.salaryAmount,
                  frequency: frequency[employment.salaryFrequency ?? 'monthly'],
                },
                businessAddress: {
                  id: `applicant-${applicantIndex}-employment-${employmentIndex}-employer-address-option-${addressIndex}`,
                  index: addressIndex,
                  addressLine: employment.line1 ?? '',
                  locality: employment.city ?? '',
                  state: employment.state?.toLocaleUpperCase() ?? '',
                  postcode: employment.postCode ?? '',
                  countryCode: 'AU',
                  addressManuallyCreated: true,
                },
                businessType: employment.businessType,
                tradingName: employment.tradingName,
                occupation: employment.occupation,
                industry: employment.industry,
                gstRegistered: employment.isGSTRegistered,
                abn: employment.australianBusinessNumber,
                soleTrader: employment.isSoleTrader,
                hasTaxReturns: employment.hasTaxReturnsLastTwoFinancialYears,
                employedSince: employment.employedSince ?? undefined,
              };
            },
          ),
        ] as Employment[],
        shortTermChange: employmentDetail?.employmentChangeInShortTerm ?? false,
        shortTermEmploymentChange: employmentDetail?.detailsOnChange ?? '',
        centrelinkPayments: {
          amount: fundsAdditionalIncome('centrelink_payments', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('centrelink_payments', applicant.id)?.frequency ?? 'monthly'],
        },
        childSupportMaintenance: {
          amount: fundsAdditionalIncome('child_support_maintenance', applicant.id)?.amount ?? 0,
          frequency:
            frequency[fundsAdditionalIncome('child_support_maintenance', applicant.id)?.frequency ?? 'monthly'],
        },
        familyAllowance: {
          amount: fundsAdditionalIncome('family_allowance', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('family_allowance', applicant.id)?.frequency ?? 'monthly'],
        },
        familyTaxBenefit: {
          amount: fundsAdditionalIncome('family_tax_benefits', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('family_tax_benefits', applicant.id)?.frequency ?? 'monthly'],
        },
        governmentPension: {
          amount: fundsAdditionalIncome('government_pension', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('government_pension', applicant.id)?.frequency ?? 'monthly'],
        },
        dividendsInvestments: {
          amount: fundsAdditionalIncome('dividends', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('dividends', applicant.id)?.frequency ?? 'monthly'],
        },
        superannuation: {
          amount: fundsAdditionalIncome('superannuation', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('superannuation', applicant.id)?.frequency ?? 'monthly'],
        },
        other: {
          amount: fundsAdditionalIncome('other', applicant.id)?.amount ?? 0,
          frequency: frequency[fundsAdditionalIncome('other', applicant.id)?.frequency ?? 'monthly'],
        },
        expectIncomeChange: incomeExpectation?.expectedChangesInNext12Months ?? false,
        effectOnIncomeChange: incomeExpectation?.financialPosition ?? '',
        incomeExpectation: {},
        employmentDetail: {},
      };
    }

    financials[`applicant-${applicantIndex}`] = {
      income,
      expenses,
      assets: [
        ...(application.fundsAssets ?? [])
          .filter((item) => item.applicantId === applicant.id && item.assetType)
          .map((asset: FundsAssetFields, assetIndex: number) => {
            if (asset.assetType === 'motor_vehicles') {
              return {
                type: asset.assetType as AssetTypeEnum,
                make: asset.vehicleMake?.name ?? '',
                model: asset.vehicleModel?.name ?? '',
                yearBought: asset.yearBought?.toString() ?? '',
                value: asset.assetAmount ?? 0,
                assetNumber: assetIndex,
              };
            }
            if (asset.assetType === 'machinery' || asset.assetType === 'equipment') {
              return {
                type: asset.assetType as AssetTypeEnum,
                description: asset.assetDescription ?? '',
                value: asset.assetAmount ?? 0,
                assetNumber: assetIndex,
              };
            }
            return {
              type: asset.assetType as AssetTypeEnum,
              institution: asset.institution?.name ?? '',
              value: asset.assetAmount ?? 0,
              assetNumber: assetIndex,
            };
          })
          .filter(
            (item) =>
              !('institution' in item) ||
              (item.institution?.toLocaleLowerCase() ?? '').trim() !== 'savings to contribute to purchase',
          ),
        ...(application.assetsProperties ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((asset, assetIndex) => {
            const addressIndex = getNextAddressIndex();
            return {
              type: 'property',
              address: {
                id: `applicant-${applicantIndex}-asset-${assetIndex}-property-address-option-${addressIndex}`,
                index: addressIndex,
                addressLine: asset.line1 ?? '',
                locality: asset.city ?? '',
                state: asset.state?.toUpperCase() ?? '',
                postcode: asset.postCode ?? '',
                countryCode: 'AU',
                addressManuallyCreated: true,
              },
              propertyValue: asset.propertyValue ?? 0,
              propertyPurpose: asset.propertyPurpose,
              rentalIncome: {
                amount: asset.rentalIncome ?? 0,
                frequency: frequency[asset.rentalIncomeFrequency ?? 'monthly'],
              },
              amountOwing: asset.amountOwed ?? 0,
              assetNumber: assetIndex,
              propertyOwnership: {
                id: `asset-${assetIndex}`,
                type: 'asset',
                share: getAssetPropertyShare(asset.id),
              },
            };
          }),
      ] as ApplicantAsset[],
      liabilities: [
        ...(application.liabilitiesCards ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((card) => {
            return {
              type: LiabilityType.CARD,
              institution: card.institution?.name ?? '',
              creditLimit: card.creditLimit ?? 0,
              amountOwing: card.amountOwed ?? 0,
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          }),
        ...(application.liabilitiesVehicles ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((vehicle) => {
            return {
              type: LiabilityType.VEHICLE,
              make: vehicle.vehicleMake?.name ?? '',
              model: vehicle.vehicleModel?.name ?? '',
              yearBought: vehicle.yearBought ?? '',
              assetValue: vehicle.assetValue ?? 0,
              liabilityLimit: vehicle.liabilityLimit ?? 0,
              amountOwing: vehicle.amountOwed,
              periodicRepayment: {
                amount: vehicle.monthlyRepayment ?? 0,
                frequency: 'month',
              },
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          }),
        ...(application.liabilitiesOthers ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((other) => {
            return {
              type: LiabilityType.OTHER,
              details: other.details ?? '',
              liabilityLimit: other.liabilityLimit ?? 0,
              amountOwing: other.amountOwed ?? 0,
              periodicRepayment: {
                amount: other.monthlyRepayment ?? 0,
                frequency: 'month',
              },
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          })
          .filter((item) => item.details && item.amountOwing),
      ] as ApplicantLiability[],
    };
  }
  return financials;
};

export const applicationProgress = (
  application: ApplicationDto,
  expenseTypes: ExpenseTypes,
  userType: AppUser,
): LoanApplication => {
  let lastAddressIndex = 0;
  const getNextAddressIndex = () => {
    lastAddressIndex++;
    return lastAddressIndex;
  };

  const loanType = () => {
    if (
      application.incomingProperties.find(
        (item) =>
          item.propertyType === PropertyType.VACANT_LAND ||
          item.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME,
      )
    ) {
      return LoanType.SINGLE_SECURITY;
    }
    if (application.loanType === 'buy_now') {
      return LoanType.BRIDGING;
    } else if (application.loanType === 'equity_release') {
      return LoanType.EQUITY_RELEASE;
    } else if (application.loanType === 'single_security') {
      return LoanType.SINGLE_SECURITY;
    }
    return undefined;
  };

  // making sure primary applicant is at index 0
  const primaryApplicant = application.applicants.find((item) => item.applicantType === 'primary');
  if (primaryApplicant) {
    application.applicants = [
      primaryApplicant,
      ...application.applicants.filter((applicant) => applicant.applicantType !== 'primary'),
    ];
  }

  const { additionalFundsRequired } = application;

  let additionalFundsRequiredProperties: AdditionalFundsRequiredProperty | undefined = undefined;
  let incomingProperties: IncomingProperty[] = application.incomingProperties.map((property, index) => {
    const addressIndex = getNextAddressIndex();
    return {
      id: `incoming-property-${index}`,
      settlementDate: application?.settlementDate ?? '',
      propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
      idsType: mapIdsTypeFromApi(property.idsType).idsType,
      commercialType: mapIdsTypeFromApi(property.idsType).commercialType,
      propertyLeased: property.isPropertyLeased,
      address: {
        id: `incoming-property-${index}-incoming-property-address-option-${addressIndex}`,
        index: addressIndex,
        addressLine: property.line1 ?? '',
        locality: property.city ?? '',
        state: property.state?.toLocaleUpperCase() ?? '',
        postcode: property.postCode ?? '',
        countryCode: 'AU',
        addressManuallyCreated: true,
      },
      propertyPurpose: property.propertyPurpose,
      contractForSale: property.contractForSale,
      purchaseAmount: property.purchasePrice,
      depositAmount: property.depositAmount,
      depositType: property.depositPaidType,
      stampDutyStatus: property.stampDutyStatus,
      stampDuty: property.stampDuty,
      securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
      receiveRentalIncome: property.rentalIncomeIsTaxable,
      rentalIncome: {
        amount: property.rentalIncome ?? 0,
        frequency: frequency[property.rentalIncomeFrequency ?? 'monthly'],
      },
      transferFeeCovered: property.transferFeeCovered,
    };
  });
  if (loanType() === LoanType.SINGLE_SECURITY) {
    const property = application.additionalFundsRequiredProperties as AdditionalFundsRequiredPropertyDto;
    const addressIndex = getNextAddressIndex();
    const singleSecurityPropertyAddress = {
      id: `single-security-address-option-${addressIndex}`,
      index: addressIndex,
      addressLine: property?.line1 ?? '',
      locality: property?.city ?? '',
      state: property?.state?.toLocaleUpperCase() ?? '',
      postcode: property?.postCode ?? '',
      countryCode: 'AU',
      addressManuallyCreated: true,
    };

    let reasonForFundsOption: AdditionalFundsPurposeEnum = AdditionalFundsPurpose.OTHER;
    let propertyType: PropertyTypeEnum | null = incomingProperties[0]?.propertyType ?? null;
    let addressRequired = false;
    const loanPurpose = additionalFundsRequired.additionalFundsPurpose?.title?.toLocaleLowerCase() ?? '';
    if (loanPurpose === 'purchase retirement, land lease or community home') {
      reasonForFundsOption = AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME;
      propertyType = PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME;
      addressRequired = true;
    } else if (loanPurpose === 'cosmetic improvements before selling') {
      reasonForFundsOption = AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING;
    } else if (loanPurpose === 'purchase vacant land') {
      reasonForFundsOption = AdditionalFundsPurpose.PURCHASE_VACANT_LAND;
      propertyType = PropertyType.VACANT_LAND;
      addressRequired = true;
    }

    const fundsToContributeAmount = application.calculatedValue?.savingsToContributeToPurchase ?? 0;

    additionalFundsRequiredProperties = {
      isThereContractForSale: property?.contractForSale ?? false,
      isAdditionalFundsRequired: additionalFundsRequired.id !== undefined && additionalFundsRequired.id !== null,
      reasonForFundsOption,
      propertyType: property?.propertyType ?? undefined,
      propertyPurpose: property?.propertyPurpose ?? undefined,
      ...singleSecurityPropertyAddress,
      propertyAddress: addressRequired ? singleSecurityPropertyAddress : undefined,
      contributingFundsTowardsPurchase: fundsToContributeAmount !== 0,
      fundsToContributeAmount,
      settlementDate: additionalFundsRequired.fundsRequiredDate ?? '',
      purchasePrice: property?.purchasePrice ?? 0,
      depositAmount: property?.depositAmount ?? 0,
      depositType: property?.depositPaidType ?? undefined,
      stampDutyStatus: property?.stampDutyStatus ?? undefined,
      transferFeeCovered: property?.transferFeeCovered ?? false,
      description: stripHtml(additionalFundsRequired?.description ?? ''),
      fundsRequiredDate: additionalFundsRequired.fundsRequiredDate,
      fundsRequiredAmount: additionalFundsRequired.fundsRequiredAmount,
      additionalFundsPurposeId: additionalFundsRequired.additionalFundsPurposeId,
    } as AdditionalFundsRequiredProperty;
    if (incomingProperties.length === 0) {
      incomingProperties = [
        {
          id: `incoming-property-0`,
          settlementDate: additionalFundsRequired.fundsRequiredDate ?? '',
          propertyType: propertyType === null ? PropertyType.RESIDENTIAL : propertyType,
          address: propertyType === null ? defaultAddress() : singleSecurityPropertyAddress,
          contractForSale: propertyType === null ? true : property.contractForSale,
          purchaseAmount: additionalFundsRequired.fundsRequiredAmount ?? 0,
          depositAmount: propertyType === null ? 0 : property.depositAmount,
          depositType: propertyType === null ? undefined : property.depositPaidType,
          stampDutyStatus: propertyType === null ? undefined : property.stampDutyStatus,
          receiveRentalIncome: false,
          securityHeldStatus: 'security_not_held',
          transferFeeCovered: propertyType === null ? undefined : property.transferFeeCovered,
        },
      ];
    }
  }

  return {
    additionalFundsRequired: {
      description: stripHtml(additionalFundsRequired?.description ?? ''),
      fundsRequiredAmount: additionalFundsRequired?.fundsRequiredAmount ?? 0,
      additionalFundsPurposeId: additionalFundsRequired?.additionalFundsPurposeId ?? '',
      fundsRequiredDate: additionalFundsRequired?.fundsRequiredDate ?? '',
      isAdditionalFundsRequired: (additionalFundsRequired?.additionalFundsPurposeId ?? '').toString().length > 0,
    },
    structuralVersionNumber: 2,
    applicationId: 'application',
    actionType: 'createNew',
    userType: userType,
    additionalFundsRequiredProperties,
    incomingProperties,
    outgoingProperties: application.outgoingProperties.map((property, index) => {
      const addressIndex = getNextAddressIndex();
      return {
        id: `outgoing-property-${index}`,
        propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
        address: {
          id: `outgoing-property-${index}-outgoing-property-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: property.line1 ?? '',
          locality: property.city ?? '',
          state: property.state?.toLocaleUpperCase() ?? '',
          postcode: property.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: true,
        },
        propertyPurpose: property.propertyPurpose ?? 'investment_property',
        outstandingLoanAmount: property.outstandingMortgageAmount ?? 0,
        saleStatus: property.soldStatus ?? SaleStatus.NONE,
        settlementDate: property.settlementDate ?? '',
        saleAmount: property.estimatedValuation ?? 0,
        depositAmount: property.depositAmountReceived ?? 0,
        depositInTrust: property.depositHeldInTrust ?? false,
        securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
        idsType: mapIdsTypeFromApi(property.idsType).idsType,
        commercialType: mapIdsTypeFromApi(property.idsType).commercialType,
        propertyLeased: property.isPropertyLeased,
      };
    }),
    supportingProperties: (application.supportingProperties ?? []).map((property, index) => {
      const addressIndex = getNextAddressIndex();
      return {
        id: `supporting-property-${index}`,
        propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
        address: {
          id: `supporting-property-${index}-supporting-property-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: property.line1 ?? '',
          locality: property.city ?? '',
          state: property.state?.toLocaleUpperCase() ?? '',
          postcode: property.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: true,
        },
        propertyPurpose: property.propertyPurpose ?? 'owner_occupied',
        outstandingLoanAmount: property.outstandingMortgageAmount ?? 0,
        supportingPropertyValue: property.estimatedValuation ?? 0,
        idsType: mapIdsTypeFromApi(property.idsType).idsType,
        commercialType: mapIdsTypeFromApi(property.idsType).commercialType ?? '',
        propertyLeased: property.isPropertyLeased,
        receiveRentalIncome: property.rentalIncomeIsTaxable,
        rentalIncome: {
          amount: property.rentalIncome ?? 0,
          frequency: frequency[property.rentalIncomeFrequency ?? 'monthly'],
        },
        securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
      };
    }),
    applicants: application.applicants.map((applicant, index) => {
      const addressIndex = getNextAddressIndex();
      let relationshipToPrimary = null;
      const applicantRelationship = (applicant.applicantRelationship ?? []).find(
        (item) => item.applicantId === applicant.id,
      );
      const relatedApplicantIndex: number = applicantRelationship
        ? application.applicants.findIndex((item) => item.id === applicantRelationship.relatedApplicantId)
        : -1;
      let relationshipOtherInfo = applicantRelationship?.otherRelationshipDescription ?? '';

      if (applicant.applicantType !== 'primary') {
        relationshipOtherInfo = applicantRelationship?.otherRelationshipDescription ?? '';
        relationshipToPrimary = applicantRelationship?.relationship;
      }

      return {
        id: `applicant-${index}`,
        type: applicant.applicantType ?? 'secondary',
        firstName: applicant.firstName ?? '',
        middleName: applicant.middleName ?? '',
        lastName: applicant.lastName ?? '',
        preferredName: applicant.preferredName ?? applicant.firstName ?? '',
        gender: applicant.gender ?? 'prefer_not_to_say',
        genderOther: '',
        birthdate: applicant.dateOfBirth ? formatDateTz(applicant.dateOfBirth) : '',
        address: {
          id: `applicant-${index}-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: applicant.line1 ?? '',
          locality: applicant.city ?? '',
          state: applicant.state?.toLocaleUpperCase() ?? '',
          postcode: applicant.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: true,
        },
        mobile: applicant.mobilePhoneNumber ?? '',
        email: applicant.email ?? '',
        confirmEmail: applicant.email ?? '',
        numberOfDependants: applicant.numberOfDependants ?? 0,
        relationshipStatus: applicant.relationshipStatus ?? 'single',
        relationshipOtherInfo,
        relationshipToPrimary: relationshipToPrimary ?? undefined,
        relatedApplicantIndex,
      } as unknown as Applicant;
    }),
    propertiesOwnership: [
      ...application.incomingProperties.map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = application.propertiesOwnership.find(
            (item) => item.applicantId === applicant.id && item.incomingPropertyId === property.id,
          );
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `incoming-property-${propertyIndex}`,
          type: 'incoming' as PropertyOwnershipType,
          share,
        };
      }),
      ...application.outgoingProperties.map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = application.propertiesOwnership.find(
            (item) => item.applicantId === applicant.id && item.outgoingPropertyId === property.id,
          );
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `outgoing-property-${propertyIndex}`,
          type: 'outgoing' as PropertyOwnershipType,
          share,
        };
      }),
      ...(application.supportingProperties ?? []).map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = (
            application.propertiesOwnership as ((typeof application.propertiesOwnership)[number] & {
              supportingPropertyId?: string;
            })[]
          ).find((item) => item.applicantId === applicant.id && item.supportingPropertyId === property.id);
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `supporting-property-${propertyIndex}`,
          type: 'supporting' as PropertyOwnershipType,
          share,
        };
      }),
    ],
    financials: mapApplicantFinancials(application, expenseTypes),
    endDebt: (application.calculatedValue?.endDebt ?? 0) * -1,
    netSurplus: (application.calculatedValue as { netSurplus?: number } | undefined)?.netSurplus ?? 0,
    verificationCode: '',
    brokerId: application.brokerId,
    summaryNotes: '',
    utmMetrics: {} as UtmMetricDto,
    allowNoContractOfSale: false,
    brokerReferenceNumber: '',
    brokerFullName: '',
    brokerEmail: '',
    loanType: loanType(),
    settlementDate: application.settlementDate,
    loanDetails: {
      savingsAmount: application.calculatedValue?.savingsToContributeToPurchase ?? 0,
      giftedAmount: application.calculatedValue?.totalTowardsPurchaseGiftedFunds ?? 0,
      reasonDescription: '',
      brokerFeePercentage: 0,
      borrowAmount: 0,
      calcLoanAmount: application.calculatedValue?.loanAmount ?? 0,
    },
    loanTermMonths: application.calculatedValue?.loanTermMonths,
    productType: application.productType,
  };
};
